import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import firebaseApp from '../firebase';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [appToken, setAppToken] = useState(() => localStorage.getItem('appToken'));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const handleForceSignout = () => auth.signOut();
    const handleDisabled = (e) => {
      setAuthError(e.detail || 'Your account has been disabled. Please contact admin.');
      auth.signOut();
    };
    window.addEventListener('auth:signout', handleForceSignout);
    window.addEventListener('auth:disabled', handleDisabled);
    return () => {
      window.removeEventListener('auth:signout', handleForceSignout);
      window.removeEventListener('auth:disabled', handleDisabled);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          console.log('Got Firebase ID token, calling /auth/verify...');
          const { data } = await api.post('/auth/verify', { idToken });
          console.log('Auth verify success:', data.user);
          setUser(data.user);
          setAppToken(data.token);
          setAuthError('');
          localStorage.setItem('appToken', data.token);
        } catch (error) {
          const msg = error.response?.data?.error || error.message || 'Auth failed';
          console.error('Auth verification failed:', msg, error);
          setAuthError(msg);
          setUser(null);
          setAppToken(null);
          localStorage.removeItem('appToken');
          // Sign out of Firebase so onAuthStateChanged fires with null
          // and stops retrying - prevents the infinite loop
          try { await auth.signOut(); } catch {}
        }
      } else {
        setUser(null);
        setAppToken(null);
        setAuthError('');
        localStorage.removeItem('appToken');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    await auth.signOut();
    setUser(null);
    setAppToken(null);
    localStorage.removeItem('appToken');
  };

  // Register for push notifications after login
  useEffect(() => {
    if (!user || !appToken) return;
    const registerPush = async () => {
      try {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          console.log('[Push] Not supported in this browser');
          return;
        }

        const permission = Notification.permission === 'granted'
          ? 'granted'
          : await Notification.requestPermission();
        console.log('[Push] Permission:', permission);
        if (permission !== 'granted') return;

        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        console.log('[Push] VAPID key present:', !!vapidKey);
        if (!vapidKey) return;

        // Register the service worker explicitly to ensure latest version
        const swReg = await navigator.serviceWorker.register('/service-worker.js', { updateViaCache: 'none' });
        console.log('[Push] Service worker registered, scope:', swReg.scope);

        // Wait for it to be active
        await navigator.serviceWorker.ready;

        const messaging = getMessaging(firebaseApp);
        const fcmToken = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
        console.log('[Push] FCM token:', fcmToken ? fcmToken.slice(0, 20) + '...' : 'NONE');

        if (fcmToken) {
          await api.post('/user/fcm-token', { token: fcmToken });
          console.log('[Push] Token saved to server');
        }

        // Foreground messages — suppress system notification (handled by in-app toast)
        onMessage(messaging, (payload) => {
          console.log('[Push] Foreground message received:', payload);
        });
      } catch (err) {
        console.error('[Push] Registration failed:', err);
      }
    };
    registerPush();
  }, [user, appToken]);

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/user/profile');
      setUser(data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, appToken, loading, authError, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
